import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Button from '@/components/common/Button';
import Modal from './Modal';
import { DeleteConfirmationModalProps } from '../../types/ModalTypes';

const DeleteConfirmationContent = ({
    entityType,
    onConfirm,
    onClose
}: {
    entityType: string;
    onConfirm: () => Promise<void>;
    onClose: () => void;
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    return (
        <div className="p-4">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete this {entityType}?
            </p>
            <div className="flex justify-center gap-4">
                <Button
                    btnStyle="ghost"
                    onClick={onClose}
                    className="px-4"
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    btnStyle="brandBtn"
                    onClick={handleConfirm}
                    className="px-4 min-w-[80px] flex items-center justify-center"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        'Delete'
                    )}
                </Button>
            </div>
        </div>
    );
};

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    entityId,
    entityType,
    deleteFunction,
    isOpen,
    onClose
}) => {
    const handleConfirm = async () => {
        try {
            // Check if deleteFunction expects an id parameter
            if (deleteFunction.length > 0) {
                await deleteFunction(String(entityId));
            } else {
                await (deleteFunction as () => Promise<void>)();
            }
            onClose();
        } catch {
            // Error handling is done in the parent component
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            position="center"
            title={`Delete ${entityType}`}
            showBackdrop={true}
            className="max-w-md"
        >
            <DeleteConfirmationContent
                entityType={entityType}
                onConfirm={handleConfirm}
                onClose={onClose}
            />
        </Modal>
    );
};

export default DeleteConfirmationModal;